import { useState } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import { Send, CheckCircle } from 'lucide-react';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';

const feedbackSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  message: z.string().trim().min(1).max(2000),
});

const FeedbackForm = () => {
  const { lang, t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const title = lang === 'ru'
    ? (settings?.contact_title_ru) || 'Давайте работать вместе'
    : (settings?.contact_title_en) || "Let's work together";

  const subtitle = lang === 'ru'
    ? (settings?.contact_subtitle_ru) || 'Расскажите о вашем проекте'
    : (settings?.contact_subtitle_en) || 'Tell us about your project';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const parsed = feedbackSchema.safeParse(form);
    if (!parsed.success) {
      setError(t('Пожалуйста, заполните все поля корректно', 'Please fill in all fields correctly'));
      return;
    }

    setSending(true);
    const id = crypto.randomUUID();
    const { error: dbError } = await supabase.from('feedback_messages').insert([{
      id,
      name: parsed.data.name,
      email: parsed.data.email,
      message: parsed.data.message,
    }]);
    
    if (!dbError) {
      // Send confirmation email to the person who submitted the form
      await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateName: 'contact-form-confirmation',
          recipientEmail: parsed.data.email,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name: parsed.data.name },
        },
      });
    }

    setSending(false);

    if (dbError) {
      setError(t('Ошибка отправки. Попробуйте позже.', 'Sending error. Please try again later.'));
    } else {
      setSent(true);
      setForm({ name: '', email: '', message: '' });
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative">
      <div className="container max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-primary font-display text-sm font-medium tracking-[0.3em] uppercase mb-4">
            {t('Контакт', 'Contact')}
          </p>
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-4">
            {title}
          </h2>
          <p className="text-muted-foreground text-lg">
            {subtitle}
          </p>
        </motion.div>

        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <CheckCircle className="mx-auto text-primary mb-4" size={48} />
            <p className="text-xl font-display font-bold mb-2">
              {t('Спасибо!', 'Thank you!')}
            </p>
            <p className="text-muted-foreground">
              {t('Мы свяжемся с вами в ближайшее время', "We'll get back to you soon")}
            </p>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('Имя', 'Name')}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                  placeholder={t('Ваше имя', 'Your name')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                  placeholder="email@example.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                {t('Сообщение', 'Message')}
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all resize-none"
                placeholder={t('Расскажите о вашем проекте...', 'Tell us about your project...')}
                required
              />
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <button
              type="submit"
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-display font-bold text-sm tracking-wide rounded-full hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:hover:scale-100 glow"
            >
              {sending ? t('Отправка...', 'Sending...') : t('Отправить', 'Send')}
              <Send size={16} />
            </button>
            <a
              href="https://t.me/sinnerkot"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-display font-bold text-sm tracking-wide rounded-full hover:scale-[1.02] transition-transform"
            >
              {t('Написать напрямую', 'Message directly')}
              <Send size={16} />
            </a>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default FeedbackForm;
